import { where } from "firebase/firestore";
import React from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FaCopy } from "react-icons/fa";

import { useCollectionGroupById } from "../../api/firestoreWrappers.generated.jsx";

function attachJsonContext(lines) {
    // Tracks the currently active JSON key path based on indentation/nesting level.
    // Example:
    // {
    //   "a": {
    //     "b": "x"
    //   }
    // }
    // When rendering `"b": "x"`, the path becomes ["a", "b"].
    const keyStack = [];
    const result = [];

    for (const line of lines) {
        const text = line.text.trim();

        // Remove keys that are no longer in scope for the current nesting level.
        while (
            keyStack.length > 0 &&
            keyStack[keyStack.length - 1].level >= line.level
        ) {
            keyStack.pop();
        }

        const keyMatch = text.match(/^"([^"]+)":/);

        if (keyMatch) {
            const key = keyMatch[1];

            keyStack.push({
                key,
                level: line.level,
            });

            result.push({
                ...line,
                context: {
                    path: keyStack.map((entry) => entry.key),
                    key,
                },
            });

            continue;
        }

        // Non-key lines still inherit the current parent path.
        result.push({
            ...line,
            context: {
                path: keyStack.map((entry) => entry.key),
            },
        });
    }

    return result;
}

function buildRows(left, right) {
    const max = Math.max(left.length, right.length);

    return Array.from({ length: max }).map((_, i) => {
        const l = left[i];
        const r = right[i];

        return {
            left: l,
            right: r,
            isChanged: l?.type !== "equal" || r?.type !== "equal",
        };
    });
}

function buildSegments(rows, contextSize = 2, minCollapseSize = 4) {
    if (!rows.length) return [];

    const ranges = [];

    // Build change windows
    for (let i = 0; i < rows.length; i++) {
        if (!rows[i].isChanged) continue;

        ranges.push({
            start: Math.max(0, i - contextSize),
            end: Math.min(rows.length, i + contextSize + 1),
        });
    }

    if (ranges.length === 0) {
        return [
            {
                rows,
                collapsed: rows.length >= minCollapseSize,
                type: "context",
            },
        ];
    }

    // Merge overlapping ranges
    const mergedRanges = [];

    for (const range of ranges) {
        const last = mergedRanges[mergedRanges.length - 1];

        if (!last || range.start > last.end) {
            mergedRanges.push({ ...range });
        } else {
            last.end = Math.max(last.end, range.end);
        }
    }

    const segments = [];
    let cursor = 0;

    for (const range of mergedRanges) {
        // Unchanged gap
        if (cursor < range.start) {
            const slice = rows.slice(cursor, range.start);

            segments.push({
                rows: slice,
                collapsed: slice.length >= minCollapseSize,
                type: "context",
            });
        }

        // Changed block
        segments.push({
            rows: rows.slice(range.start, range.end),
            collapsed: false,
            type: "change",
        });

        cursor = range.end;
    }

    // Trailing gap
    if (cursor < rows.length) {
        const slice = rows.slice(cursor);

        segments.push({
            rows: slice,
            collapsed: slice.length >= minCollapseSize,
            type: "context",
        });
    }

    return segments;
}

function stripOuterObject(left, right) {
    const hasWrapper =
        left.length > 1 &&
        right.length > 1 &&
        left[0].text === "{" &&
        left[left.length - 1].text === "}" &&
        right[0].text === "{" &&
        right[right.length - 1].text === "}";

    if (!hasWrapper) return [left, right];

    const normalize = (lines) =>
        lines.slice(1, -1).map((l) => ({
            ...l,
            level: l.level - 1,
        }));

    return [normalize(left), normalize(right)];
}

export function DiffViewer({
    diff,
    isCreate,
    isUpdate,
    isDelete,
    forceSingleColumn,
}) {
    const [expanded, setExpanded] = React.useState({});

    const [rawLeft, rawRight] = diff;

    const left = attachJsonContext(rawLeft);
    const right = attachJsonContext(rawRight);

    const [normalizedLeft, normalizedRight] = stripOuterObject(left, right);

    const rows = buildRows(normalizedLeft, normalizedRight);

    const showOld = isUpdate || isDelete;
    const showNew = isUpdate || isCreate;

    const segments =
        isCreate || isDelete
            ? [
                  {
                      rows,
                      collapsed: true,
                      type: isCreate ? "create" : "delete",
                  },
              ]
            : buildSegments(rows, 2);

    const expandSegment = (index) => {
        setExpanded((prev) => ({
            ...prev,
            [index]: true,
        }));
    };

    return (
        <div className="container-fluid p-0">
            {segments.map((segment, i) => {
                const isCollapsed = segment.collapsed && !expanded[i];

                if (isCollapsed) {
                    return (
                        <CollapsedGap
                            // biome-ignore lint/suspicious/noArrayIndexKey: Diff segments are derived positional data and expansion state is intentionally indexed by segment position.
                            key={i}
                            count={segment.rows.length}
                            type={segment.type}
                            onClick={() => expandSegment(i)}
                        />
                    );
                }

                return segment.rows.map((row, j) => (
                    <RowRenderer
                        // biome-ignore lint/suspicious/noArrayIndexKey: Diff rows are derived positional data with no stable identifier.
                        key={`${i}-${j}`}
                        row={row}
                        showOld={showOld}
                        showNew={showNew}
                        forceSingleColumn={forceSingleColumn}
                    />
                ));
            })}
        </div>
    );
}

function CollapsedGap({ count, onClick, type }) {
    const bgClassMap = {
        create: "bg-success-subtle",
        delete: "bg-danger-subtle",
        context: "bg-secondary-subtle",
    };

    const labelMap = {
        create: `Show created document (${count} lines)`,
        delete: `Show deleted document (${count} lines)`,
        context: `▶ ${count} unchanged lines (click to expand)`,
    };

    return (
        <button
            type="button"
            onClick={onClick}
            className={`w-100 text-center small py-1 ${bgClassMap[type]} ${type !== "context" ? "py-2 fw-medium" : ""}`}
            style={{
                borderTop: "1px dashed #bbb",
                borderBottom: "1px dashed #bbb",
                fontStyle: "italic",
                borderLeft: "none",
                borderRight: "none",
                background: "none",
            }}
        >
            {labelMap[type]}
        </button>
    );
}

function RowRenderer({ row, showOld, showNew, forceSingleColumn }) {
    const desktopHiddenClass = forceSingleColumn
        ? "d-none"
        : "d-none d-md-flex";
    const mobileClass = forceSingleColumn
        ? "d-flex flex-column"
        : "d-flex flex-column d-md-none";

    return (
        <>
            <div
                className={`row g-0 align-items-stretch ${desktopHiddenClass}`}
            >
                {showOld && (
                    <div className={showNew ? "col-6" : "col-12"}>
                        <DiffLine side="old" line={row.left} />
                    </div>
                )}

                {showNew && (
                    <div className={showOld ? "col-6" : "col-12"}>
                        <DiffLine side="new" line={row.right} />
                    </div>
                )}
            </div>

            <div className={mobileClass}>
                <SingleColumnRow
                    row={row}
                    showOld={showOld}
                    showNew={showNew}
                />
            </div>
        </>
    );
}

function SingleColumnRow({ row, showOld, showNew }) {
    const { left, right, isChanged } = row;

    const leftExists = left && left.text !== "";
    const rightExists = right && right.text !== "";

    // Equal → render once
    if (!isChanged) {
        return leftExists ? <DiffLine side="old" line={left} /> : null;
    }

    // Only one side exists
    if (leftExists && !rightExists) {
        return <DiffLine side="old" line={left} />;
    }

    if (!leftExists && rightExists) {
        return <DiffLine side="new" line={right} />;
    }

    // Both exist and differ
    const parsedLeft = parseJsonLine(left.text);
    const parsedRight = parseJsonLine(right.text);

    const isSameKeyValue =
        parsedLeft.type === "keyValue" &&
        parsedRight.type === "keyValue" &&
        parsedLeft.key === parsedRight.key;

    const isPrimitiveValue = (parsed) =>
        parsed.valueType === "string" ||
        parsed.valueType === "number" ||
        parsed.valueType === "boolean" ||
        parsed.value === "null";

    const isStructuralValue = (parsed) =>
        typeof parsed.value === "string" &&
        (parsed.value === "{" || parsed.value === "[");

    if (
        isSameKeyValue &&
        isPrimitiveValue(parsedLeft) &&
        isPrimitiveValue(parsedRight) &&
        !isStructuralValue(parsedLeft) &&
        !isStructuralValue(parsedRight)
    ) {
        return (
            <DiffInlineCombined
                line={left}
                leftParsed={parsedLeft}
                rightParsed={parsedRight}
            />
        );
    }

    // When the old/left side is the start of a block and the new/right side isn't
    // we'll show new first to make it look better RLLLL instead of LRLLL
    const showNewFirst =
        isSameKeyValue &&
        isStructuralValue(parsedLeft) &&
        isPrimitiveValue(parsedRight);

    if (showNewFirst) {
        return (
            <>
                {showNew && rightExists && <DiffLine side="new" line={right} />}
                {showOld && leftExists && <DiffLine side="old" line={left} />}
            </>
        );
    }

    // fallback
    return (
        <>
            {showOld && leftExists && <DiffLine side="old" line={left} />}
            {showNew && rightExists && <DiffLine side="new" line={right} />}
        </>
    );
}

const DIFF_VARIANTS = {
    neutral: { prefix: "  ", bg: "" },
    add: { prefix: "+ ", bg: "bg-success-subtle" },
    remove: { prefix: "- ", bg: "bg-danger-subtle" },
};

function getDiffStyle({ line, side }) {
    if (!line) return DIFF_VARIANTS.neutral;

    const isEmpty = !line.text || line.text.trim() === "";

    if (line.type === "add") return DIFF_VARIANTS.add;
    if (line.type === "remove") return DIFF_VARIANTS.remove;

    if (line.type === "modify" && !isEmpty) {
        return side === "old" ? DIFF_VARIANTS.remove : DIFF_VARIANTS.add;
    }

    return DIFF_VARIANTS.neutral;
}

function DiffInlineCombined({ line, leftParsed, rightParsed }) {
    return (
        <div
            className="d-flex flex-fill px-2 py-0 font-monospace small"
            style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
            }}
        >
            {/* ~ prefix */}
            <div style={{ width: 16, flexShrink: 0 }}>{"~ "}</div>

            <div style={{ paddingLeft: line.level * 16 }}>
                &quot;{leftParsed.key}&quot;: {/* old value */}
                <span className="bg-danger-subtle px-1">
                    {renderValueInline(leftParsed, line.context)}
                </span>
                <span className="mx-2 text-muted">→</span>
                {/* new value */}
                <span className="bg-success-subtle px-1">
                    {renderValueInline(rightParsed, line.context)}
                </span>
                {rightParsed.hasComma && ","}
            </div>
        </div>
    );
}

function DiffLine({ side, line }) {
    if (!line) {
        return <div className="flex-fill px-2 py-0 small" />;
    }

    const { prefix, bg } = getDiffStyle({ line, side });

    return (
        <div
            className={`d-flex flex-fill px-2 py-0 font-monospace small ${bg}`}
            style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
            }}
        >
            {/* Prefix */}
            <div style={{ width: 16, flexShrink: 0 }}>{prefix}</div>

            {/* Content */}
            <div style={{ paddingLeft: line.level * 16 }}>
                <RenderLineText text={line.text} context={line.context} />
            </div>
        </div>
    );
}

function parseJsonLine(text) {
    const keyValueMatch = text.match(/^"([^"]+)":\s*(.*)$/);
    const stringOnlyMatch = text.match(/^"([^"]+)"$/);

    if (stringOnlyMatch) {
        return { type: "string", key: null, value: stringOnlyMatch[1] };
    }

    if (!keyValueMatch) return { type: "raw", text };

    let rawValue = keyValueMatch[2];
    const hasComma = rawValue.endsWith(",");
    if (hasComma) rawValue = rawValue.slice(0, -1);

    const key = keyValueMatch[1];

    // detect value type
    if (/^".*"$/.test(rawValue)) {
        return {
            type: "keyValue",
            key,
            value: rawValue.slice(1, -1),
            valueType: "string",
            hasComma,
        };
    }

    if (/^(true|false)$/.test(rawValue)) {
        return {
            type: "keyValue",
            key,
            value: rawValue === "true",
            valueType: "boolean",
            hasComma,
        };
    }

    if (/^-?\d+(\.\d+)?$/.test(rawValue)) {
        return {
            type: "keyValue",
            key,
            value: Number(rawValue),
            valueType: "number",
            hasComma,
        };
    }

    // fallback (arrays, objects, etc.)
    return {
        type: "keyValue",
        key,
        value: rawValue,
        valueType: "raw",
        hasComma,
    };
}

function renderValueInline(parsed, context) {
    return renderFormattedValue({
        key: parsed.key,
        value: parsed.value,
        valueType: parsed.valueType,
        context,
    });
}

function RenderLineText({ text, context }) {
    const parsed = parseJsonLine(text);

    if (parsed.type === "raw") return text;

    if (parsed.type === "string") {
        return renderFormattedValue({
            key: null,
            value: parsed.value,
            valueType: parsed.valueType,
            context,
        });
    }

    return (
        <>
            &quot;{parsed.key}&quot;:{" "}
            {renderFormattedValue({
                key: parsed.key,
                value: parsed.value,
                valueType: parsed.valueType,
                context,
            })}
            {parsed.hasComma && ","}
        </>
    );
}

const getParent = (ctx) => ctx.path.at(-2);
const getArrayParent = (ctx) => ctx.path.at(-1);

const directIdMap = {
    organizationId: "organizationId",
    locationId: "locationId",
    payloadId: "payloadId",
    taskId: "taskId",
    dockId: "dockId",
};

const parentIdMap = {
    apps: "appId",
    docks: "dockId",
    dependencies: "appId",
    "settings.model_runner.models": "modelId",
    models: "modelId",
};

function renderFormattedValue({ key, value, valueType, context }) {
    const parent = getParent(context);
    const arrayParent = getArrayParent(context);

    // --- direct key mapping ---
    if (directIdMap[key]) {
        return <NameFromId settingName={directIdMap[key]} id={value} />;
    }

    // --- id fields with parent ---
    if (key === "id" && parentIdMap[parent]) {
        return <NameFromId settingName={parentIdMap[parent]} id={value} />;
    }

    // --- array values (no key, only parent) ---
    if (!key && parentIdMap[arrayParent]) {
        return <NameFromId settingName={parentIdMap[arrayParent]} id={value} />;
    }

    if (valueType === "string") return `"${value}"`;
    if (valueType === "number" || valueType === "boolean") return String(value);
    return value; // raw
}

function NameFromId({ settingName, id }) {
    const [copiedValue, setCopiedValue] = React.useState(null);

    const collectionMap = {
        organizationId: "organizations",
        locationId: "locations",
        payloadId: "payloads",
        taskId: "tasks",
        dockId: "docks",
        appId: "apps",
        modelId: "models",
    };

    const collection = collectionMap[settingName];

    const [doc] = useCollectionGroupById(collection, [where("id", "==", id)], {
        mode: "once",
    });

    const name = doc?.[0]?.name;

    const isCopied = copiedValue === id;

    return (
        <>
            {name === undefined ? (
                `"${id}"`
            ) : (
                <>
                    {name}

                    <OverlayTrigger
                        placement="top"
                        delay={{ show: 250 }}
                        overlay={
                            <Tooltip>
                                {isCopied
                                    ? "Copied!"
                                    : `Copy ${settingName}: ${id}`}
                            </Tooltip>
                        }
                    >
                        {/* biome-ignore lint/a11y/useSemanticElements: Interactive span preserves the inline OverlayTrigger styling while providing button keyboard semantics. */}
                        <span
                            role="button"
                            tabIndex={0}
                            className={`ms-1 ${isCopied ? "text-success" : "text-muted"}`}
                            style={{
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                transition: "color 0.15s",
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(String(id));
                                setCopiedValue(id);
                                setTimeout(() => setCopiedValue(null), 1500);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(String(id));
                                    setCopiedValue(id);
                                    setTimeout(
                                        () => setCopiedValue(null),
                                        1500,
                                    );
                                }
                            }}
                        >
                            <FaCopy size={14} />
                        </span>
                    </OverlayTrigger>
                </>
            )}
        </>
    );
}
